import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaymentDialog } from "@/components/PaymentDialog";
import { UtensilsCrossed, AlertTriangle, Trash2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const PREP_PREFS = ["No gravy", "Gravy on the side", "Sauce on the side", "No cheese", "No beef", "No pork", "Vegetarian preference"];
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => new Date(d + "T12:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" });

export default function Dinner() {
  const [kids, setKids] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [childId, setChildId] = useState("");
  const [activeDate, setActiveDate] = useState("");
  const [meals, setMeals] = useState([]);
  const [sel, setSel] = useState({ meal_id: "", portion: "", side: "", prefs: [], notes: "" });
  const [payOrder, setPayOrder] = useState(null);
  const [packs, setPacks] = useState({});
  const [balances, setBalances] = useState({});
  const [combo, setCombo] = useState({ active: false, label: "Dinner + Home Drop-Off" });
  const [useCredit, setUseCredit] = useState(false);
  const [sugg, setSugg] = useState({ suggestion: "", prep_notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/children").then((r) => r.data).catch(() => []),
      api.get("/bookings").then((r) => (Array.isArray(r.data) ? r.data : r.data.bookings || [])).catch(() => []),
      api.get("/dinner/orders").then((r) => r.data).catch(() => []),
      api.get("/dinner/packs").then((r) => r.data).catch(() => ({ packs: {}, balances: {}, combo: {} })),
    ]).then(([k, b, o, p]) => {
      setKids(k); setBookings(b); setOrders(o);
      setPacks(p.packs || {}); setBalances(p.balances || {}); setCombo(p.combo || { active: false });
      if (k.length && !childId) setChildId(k[0].id);
    });
  }, [childId]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const child = kids.find((k) => k.id === childId);
  const upcoming = [...new Set(bookings.filter((b) => b.child_id === childId && b.status !== "cancelled" && b.date > today()).map((b) => b.date))].sort();
  const orderFor = (d) => orders.find((o) => o.child_id === childId && o.date === d);

  const openAdd = async (d) => {
    setActiveDate(d); setSel({ meal_id: "", portion: "", side: "", prefs: [], notes: "" }); setMeals([]); setUseCredit(false);
    try { const { data } = await api.get(`/dinner/meals?date=${d}`); setMeals(data.meals); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const buyPack = async (packKey) => {
    try { const { data } = await api.post("/dinner/packs/buy", { child_id: childId, pack_key: packKey }); setPayOrder({ payment_order_id: data.payment_order_id, amount: data.amount, reference: data.reference }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const togglePref = (p) => setSel((s) => ({ ...s, prefs: s.prefs.includes(p) ? s.prefs.filter((x) => x !== p) : [...s.prefs, p] }));

  const submit = async () => {
    if (!sel.meal_id) return toast.error("Please choose a meal.");
    const chosenMeal = meals.find((m) => m.id === sel.meal_id);
    if (chosenMeal?.sides?.length && !sel.side) return toast.error("Please choose a side.");
    setBusy(true);
    try {
      const { data } = await api.post("/dinner/order", { child_id: childId, date: activeDate, meal_id: sel.meal_id, portion: sel.portion, side: sel.side, prep_prefs: sel.prefs, notes: sel.notes, use_credit: useCredit });
      setActiveDate(""); load();
      if (data.status === "confirmed") { toast.success("Dinner booked with a credit — all set!"); }
      else { setPayOrder({ payment_order_id: data.payment_order_id, amount: data.amount, reference: data.reference }); }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const cancelDinner = async (id) => {
    try { await api.delete(`/dinner/order/${id}`); toast.success("Dinner cancelled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sendSuggestion = async () => {
    if (!sugg.suggestion.trim()) return toast.error("Please enter a suggestion.");
    try { await api.post("/dinner/suggestion", { child_id: childId, ...sugg }); toast.success("Thanks! We'll review your request."); setSugg({ suggestion: "", prep_notes: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="dinner-page">
      <div className="rounded-3xl bg-navy text-white p-6 sm:p-8 relative overflow-hidden">
        <UtensilsCrossed className="w-40 h-40 absolute -right-6 -bottom-6 text-white/5" />
        <p className="text-gold font-display font-bold tracking-widest text-xs">EXPLORER DINNER CLUB</p>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-1">PLAY. LEARN. EAT. HOME.</h1>
        <p className="text-white/70 mt-2 max-w-xl">Add dinner to your Explorer's afternoon and make evenings a little easier. Dinner is served around 5:15–5:40 PM. Pre-order only.</p>
      </div>

      {kids.length === 0 ? (
        <p className="text-slate-500">Add an explorer and book an Adventure Rock day first, then you can add dinner here.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="dinner-child-picker">
            {kids.map((k) => (
              <button key={k.id} onClick={() => { setChildId(k.id); setActiveDate(""); }} data-testid={`dinner-child-${k.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${childId === k.id ? "bg-turquoise text-navy" : "bg-white border border-slate-200 text-slate-600"}`}>
                {k.first_name}
              </button>
            ))}
          </div>

          {child?.allergies && (
            <div className="flex items-start gap-2 bg-coral/10 border border-coral/30 rounded-2xl p-3" data-testid="dinner-allergy-banner">
              <AlertTriangle className="w-5 h-5 text-coral shrink-0 mt-0.5" />
              <p className="text-sm text-coral"><b>Allergy on file for {child.first_name}:</b> {child.allergies}. Our kitchen always follows the allergy record over any meal preference. Update details in My Explorers.</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="dinner-packs">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="font-display font-bold text-navy">Dinner Plans</p>
              <span className="text-sm font-semibold text-turquoise" data-testid="credit-balance">{balances[childId] || 0} dinner credit{(balances[childId] || 0) === 1 ? "" : "s"} for {child?.first_name}</span>
            </div>
            {combo.active && <p className="text-xs text-gold font-semibold mt-1" data-testid="combo-promo">✨ {combo.label} — {combo.note || "book activities, dinner & a ride home together."}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {Object.entries(packs).map(([k, p]) => (
                <button key={k} onClick={() => buyPack(k)} data-testid={`buy-pack-${k}`} className="border border-slate-200 rounded-xl p-3 text-left hover:border-turquoise transition-colors">
                  <p className="text-sm font-semibold text-navy">{p.label}</p>
                  <p className="text-[11px] text-slate-400">{p.size} dinner{p.size === 1 ? "" : "s"}</p>
                  <p className="font-display font-extrabold text-navy mt-1">{p.price > 0 ? `$${Number(p.price).toFixed(2)}` : "TBD"}</p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Buy credits, then choose "Use a dinner credit" when adding dinner to a day.</p>
          </div>

          <div>
            <h2 className="font-display font-bold text-navy text-lg mb-2">My Explorer's Dinner</h2>
            {upcoming.length === 0 ? (
              <p className="text-slate-500 text-sm">No upcoming booked Adventure Rock days for {child?.first_name}. Book a day, then pre-order dinner here.</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map((d) => {
                  const o = orderFor(d);
                  return (
                    <div key={d} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`dinner-day-${d}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div><p className="font-semibold text-navy">{fmt(d)}</p>
                          {o ? <p className="text-sm text-turquoise font-medium">🍽 {o.meal_name}{o.side ? ` + ${o.side}` : ""}{o.prep_prefs?.length ? ` · ${o.prep_prefs.join(", ")}` : ""} <span className="text-xs text-slate-400">({o.status === "confirmed" ? "confirmed" : "payment pending"})</span></p>
                             : <p className="text-sm text-slate-400">No dinner ordered</p>}
                          {o && combo.active && ["dropoff", "both"].includes((bookings.find((b) => b.child_id === childId && b.date === d) || {}).transport) && <p className="text-[11px] font-bold text-gold mt-0.5" data-testid={`combo-badge-${d}`}>✨ {combo.label}</p>}
                        </div>
                        {o ? (
                          <Button size="sm" variant="outline" className="rounded-full text-coral border-coral/30" onClick={() => cancelDinner(o.id)} data-testid={`dinner-cancel-${d}`}><Trash2 className="w-4 h-4 mr-1" /> Cancel</Button>
                        ) : (
                          <Button size="sm" className="rounded-full bg-navy text-white font-bold" onClick={() => openAdd(d)} data-testid={`dinner-add-${d}`}>Add Dinner</Button>
                        )}
                      </div>

                      {activeDate === d && !o && (
                        <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid="dinner-meal-picker">
                          {meals.length === 0 ? <p className="text-sm text-slate-400">No meals on the menu for this day yet — check back soon.</p> : (
                            <div className="space-y-2">
                              {meals.map((m) => (
                                <div key={m.id}>
                                  <label className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer ${sel.meal_id === m.id ? "border-turquoise bg-turquoise/5" : "border-slate-200"} ${m.sold_out ? "opacity-50" : ""}`} data-testid={`dinner-meal-${m.id}`}>
                                    <input type="radio" name="meal" disabled={m.sold_out} checked={sel.meal_id === m.id} onChange={() => setSel((s) => ({ ...s, meal_id: m.id, side: "" }))} />
                                    <div className="flex-1"><p className="font-semibold text-navy">{m.name} {m.sold_out && <span className="text-xs text-coral">SOLD OUT</span>}</p><p className="text-xs text-slate-500">{m.description}</p></div>
                                    <span className="font-bold text-navy">{m.price > 0 ? `$${m.price.toFixed(2)}` : "TBD"}</span>
                                  </label>
                                  {sel.meal_id === m.id && m.sides?.length > 0 && (
                                    <div className="ml-8 mt-2" data-testid={`dinner-sides-${m.id}`}>
                                      <Label className="text-xs text-slate-500">Choose a side</Label>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {m.sides.map((sd) => (
                                          <button key={sd} type="button" onClick={() => setSel((s) => ({ ...s, side: sd }))} data-testid={`dinner-side-${sd.replace(/\s/g, "-").toLowerCase()}`}
                                            className={`text-xs px-3 py-1.5 rounded-full border ${sel.side === sd ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{sd}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              <div>
                                <Label className="text-xs text-slate-500">Preparation preferences (optional)</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {PREP_PREFS.map((p) => (
                                    <button key={p} type="button" onClick={() => togglePref(p)} data-testid={`dinner-pref-${p.replace(/\s/g, "-").toLowerCase()}`}
                                      className={`text-xs px-3 py-1.5 rounded-full border ${sel.prefs.includes(p) ? "bg-turquoise/20 border-turquoise text-navy font-semibold" : "border-slate-200 text-slate-500"}`}>{p}</button>
                                  ))}
                                </div>
                              </div>
                              <Textarea placeholder="Other notes (optional)" value={sel.notes} onChange={(e) => setSel((s) => ({ ...s, notes: e.target.value }))} className="rounded-xl" data-testid="dinner-notes" />
                              {(balances[childId] || 0) > 0 && (
                                <label className="flex items-center gap-2 text-sm bg-turquoise/10 rounded-xl px-3 py-2" data-testid="use-credit-toggle">
                                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} />
                                  Use a dinner credit ({balances[childId]} left) — no payment needed
                                </label>
                              )}
                              <div className="flex gap-2">
                                <Button onClick={submit} disabled={busy} className="rounded-full bg-turquoise text-navy font-bold" data-testid="dinner-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (useCredit ? "Book with credit" : "Add dinner & pay")}</Button>
                                <Button variant="ghost" onClick={() => setActiveDate("")} className="rounded-full text-slate-500">Close</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dinner-suggestion">
            <h3 className="font-display font-bold text-navy">DON'T SEE WHAT YOUR EXPLORER LIKES?</h3>
            <p className="text-sm text-slate-500 mt-1">Have a meal suggestion or does your child prefer something that isn't currently on our menu? Let us know. We'll review your request and do our best to accommodate it when possible.</p>
            <Label className="text-xs mt-3 block">Food preference / meal suggestion</Label>
            <Textarea value={sugg.suggestion} onChange={(e) => setSugg((s) => ({ ...s, suggestion: e.target.value }))} className="rounded-xl" data-testid="suggestion-text" />
            <Label className="text-xs mt-2 block">Anything we should know about how your child likes their food prepared?</Label>
            <Input value={sugg.prep_notes} onChange={(e) => setSugg((s) => ({ ...s, prep_notes: e.target.value }))} className="rounded-xl" data-testid="suggestion-prep" />
            <p className="text-[11px] text-slate-400 mt-2">Meal requests are subject to availability and dietary-safety requirements and are not confirmed orders until we approve them.</p>
            <Button onClick={sendSuggestion} className="mt-3 rounded-full bg-navy text-white font-bold" data-testid="suggestion-send"><Send className="w-4 h-4 mr-1" /> Send suggestion</Button>
          </div>
        </>
      )}

      <PaymentDialog order={payOrder} open={!!payOrder} onClose={() => setPayOrder(null)} onDone={load} />
    </div>
  );
}
